import { HTMLAttributes, ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { cn } from './cn';

export interface SidePanelProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  open: boolean;
  onClose: () => void;
  side?: 'left' | 'right';
  /** Dims the page and closes on an outside click. */
  backdrop?: boolean;
  /** Extra classes for the backdrop, for example `bg-transparent` for a click-catcher that does not dim. */
  backdropClassName?: string;
  /** Keeps the contents mounted (but hidden) while closed, so their state survives. */
  keepMounted?: boolean;
  children: ReactNode;
}

/**
 * A slide-over panel that does not trap focus or block the rest of the page.
 * Use it instead of `Sheet` when the panel hosts legacy widgets that portal
 * their own popovers to `document.body`; a modal dialog would make those inert.
 */
export const SidePanel = ({
  backdrop = true,
  backdropClassName,
  keepMounted = false,
  children,
  className,
  onClose,
  open,
  side = 'right',
  ...props
}: SidePanelProps) => {
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        onClose();
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onClose, open]);

  if (!open && !keepMounted) {
    return null;
  }

  return createPortal(
    <>
      {backdrop && open && (
        <div
          aria-hidden="true"
          className={cn('fixed inset-0 z-header bg-ink/40', backdropClassName)}
          onClick={onClose}
        />
      )}
      <div
        role="dialog"
        hidden={!open}
        className={cn(
          'fixed inset-y-0 z-nav max-w-full flex-col',
          open ? 'flex' : 'hidden',
          'overflow-y-auto overscroll-contain border-line bg-surface text-ink',
          'shadow-auditor-overlay',
          side === 'right' ? 'right-0 border-l' : 'left-0 border-r',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </>,
    document.body,
  );
};
